{
  "toasts": {
    "apiKeyGenerated": "API key generated successfully!",
    "apiKeyGenerateError": "Failed to generate API key",
    "apiKeyRegenerated": "API key regenerated successfully!",
    "apiKeyRegenerateError": "Failed to regenerate API key"
  },
  "page": {
    "title": "Settings",
    "subtitle": "Adjust everything about your platform in one place — visuals, emails, integrations and security."
  },
  "tabs": {
    "overview": "Overview",
    "api": "API & Security",
    "terms": "Terms of use",
    "language": "Language"
  },
  "actions": {
    "cancel": "Cancel"
  },
  "hub": {
    "recursos": {
      "title": "Features",
      "description": "Turn community, certificates and gamification on or off for your students."
    },
    "domains": {
      "title": "Addresses",
      "description": "The address your students use to get in: use the ready-made subdomain or point your own domain."
    },
    "branding": {
      "title": "Branding",
      "description": "Logo, sign-in screen image, favicon and your brand colors."
    },
    "emailTemplates": {
      "title": "Email templates",
      "description": "Edit the emails sent automatically for each event (welcome, enrollment, etc.)."
    },
    "webhooks": {
      "title": "Webhooks",
      "description": "Notify external systems when events happen (enrollment, payment, etc.)."
    },
    "surveys": {
      "title": "Surveys",
      "description": "Create surveys segmented by student, course or period — with NPS, scales and more."
    }
  },
  "overview": {
    "introTitle": "Start with what matters most",
    "introDescription": "This is the platform settings hub. Click any card below to customize a specific area. For API keys, security or uploading the terms of use contract, use the tabs above.",
    "areas": "Settings areas",
    "quickSettings": "Quick settings",
    "apiDescription": "API keys, JWT tokens and external authentication.",
    "termsDescription": "Upload the contract students accept when they sign in."
  },
  "api": {
    "copied": "Copied to clipboard!",
    "explainerTitle": "What is an \"API key\"?",
    "explainerDescription": "It is a special password that lets <bold>other systems</bold> talk to your platform (e.g. a CRM, a mobile app, a Zapier automation). Only generate one if you know where you will use it — and never share it publicly.",
    "keyTitle": "API key",
    "keyDescription": "Use this key to authenticate external calls to the platform API.",
    "saveNowTitle": "Save this key now!",
    "saveNowDescription": "This key will not be shown again. Copy it and store it somewhere safe.",
    "savedConfirm": "Got it, I saved it",
    "createdAt": "Created on {{date}}",
    "active": "Active",
    "regenerating": "Regenerating...",
    "regenerate": "Regenerate key",
    "regenerateConfirmTitle": "Regenerate API key?",
    "regenerateConfirmDescription": "The current key will be invalidated immediately. Any integration using the old key will stop working.",
    "regenerateAction": "Regenerate",
    "generating": "Generating...",
    "generate": "Generate API key",
    "tokenTitle": "Access token",
    "tokenDescription": "Your current JWT token for authenticated API calls.",
    "tokenExpiry": "This token expires in <bold>15 minutes</bold> and is renewed automatically. Use it in the header:",
    "noToken": "No token available. Please sign in again."
  },
  "terms": {
    "toasts": {
      "updated": "Contract updated successfully!",
      "uploadError": "Failed to upload contract",
      "removed": "Contract removed",
      "removeError": "Failed to remove contract",
      "urlError": "Error saving the contract link"
    },
    "explainerTitle": "Contract students accept when they sign in",
    "explainerDescription": "When a student signs in for the first time, they need to <bold>accept the terms</bold>. The file you upload here is what they will see and read. Replace it anytime — the new version applies to new acceptances.",
    "cardTitle": "Terms and Conditions contract",
    "currentContract": "Current contract",
    "openFile": "Open file",
    "remove": "Remove",
    "replace": "Replace",
    "emptyTitle": "No contract uploaded",
    "emptyDescription": "Until you upload a file or link, your students skip the terms acceptance step.",
    "upload": "Upload contract",
    "urlLabel": "Contract link",
    "urlPlaceholder": "Or paste the https link to the contract PDF",
    "saveUrl": "Use link",
    "hint": "Accepts PDF, DOC and DOCX up to 10MB. If you update the file, consider notifying students who already accepted the previous version."
  },
  "recursos": {
    "items": {
      "community": {
        "name": "Community",
        "description": "A space for students to talk, with posts, comments and events."
      },
      "certificates": {
        "name": "Certificates",
        "description": "Certificate issued when a course is completed."
      },
      "gamification": {
        "name": "Gamification",
        "description": "Points, levels and a leaderboard among students."
      },
      "assistant": {
        "name": "AI assistant",
        "description": "Builds and uploads courses to your member area by chatting with you."
      }
    },
    "toasts": {
      "updated": "Feature updated.",
      "updateError": "Could not change it."
    },
    "loading": "Loading…",
    "title": "Features",
    "subtitle": "What is available to your students.",
    "outOfPlan": "Not in plan",
    "turnOn": "Turn on {{name}}",
    "turnOff": "Turn off {{name}}",
    "notInPlan": "This feature is not included in your plan. Contact Xtreed to unlock it.",
    "community": {
      "toasts": {
        "updated": "Community address updated.",
        "saveError": "Could not save."
      },
      "label": "Community address",
      "save": "Save",
      "hint": "Leave blank to use the default Xtreed address. A custom address must be on the same domain as your members area."
    }
  },
  "domains": {
    "status": {
      "pending": "Waiting for DNS",
      "verifying": "Verifying",
      "active": "Active",
      "failed": "Not found"
    },
    "toasts": {
      "genericError": "Could not complete the action.",
      "created": "Address added. Configure DNS to activate it.",
      "verified": "Address verified.",
      "txtPending": "The TXT record has not shown up yet. DNS can take a few hours.",
      "primaryUpdated": "Primary address updated.",
      "removed": "Address removed.",
      "copyError": "Could not copy. Select and copy it manually."
    },
    "title": "Addresses",
    "subtitle": "Where your students get in. You can use the ready-made subdomain or point your own domain.",
    "placeholder": "members.yourschool.com",
    "newAddress": "New address",
    "add": "Add",
    "loading": "Loading addresses…",
    "empty": "No addresses added yet.",
    "primary": "Primary",
    "verify": "Verify",
    "makePrimary": "Make primary",
    "remove": "Remove {{domain}}",
    "dnsInstructions": "Create these two records in the DNS for <bold>{{domain}}</bold>. Propagation usually takes from a few minutes to a few hours.",
    "txtExplanation": "Proves the domain is yours",
    "cnameExplanation": "Points traffic to the platform",
    "notFound": "We could not find the record. Make sure it was published and verify again — DNS can take a while to propagate.",
    "copyValue": "Copy {{type}} record value"
  },
  "language": {
    "title": "Area language",
    "description": "Language of the whole student area: menus, buttons, messages and emails.",
    "label": "Member area language",
    "hint": "Students can't change the language. Only the team (admin and instructor) uses their own language in the dashboard.",
    "saved": "Default language updated.",
    "saveError": "Could not save the default language."
  }
}
